import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import * as maintenanceService from "../../services/maintenanceService";
import {
  MaintenanceRequest,
  MaintenanceType,
  CreateMaintenanceTypeData,
  MaintenanceAssignment,
} from "../../services/maintenanceService";

interface MaintenanceState {
  requests: MaintenanceRequest[];
  currentRequest: MaintenanceRequest | null;
  currentRequestId: string | null;
  maintenanceTypes: MaintenanceType[];
  currentMaintenanceType: MaintenanceType | null;
  maintenanceAssignments: MaintenanceAssignment[];
  currentAssignment: MaintenanceAssignment | null;
  availableAssignees: any[];
  selectedCompanyId: string | null;
  filters: any;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
  maintenanceStats: any | null;
  stats: {
    totalRequests: number;
    statusCounts: {
      pending: number;
      assigned: number;
      accepted: number;
      inProgress: number;
      onHold: number;
      completed: number;
      closed: number;
      cancelled: number;
    };
    priorityCounts: {
      low: number;
      medium: number;
      high: number;
      urgent: number;
    };
    overdueRequests: number;
    averageResponseTime: number;
    averageCompletionTime: number;
    satisfaction: {
      average: number;
      count: number;
    };
  } | null;
  isLoading: boolean;
  isLoadingTypes: boolean;
  isLoadingAssignments: boolean;
  isLoadingAssignees: boolean;
  isLoadingStats: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  error: string | null;
  success: string | null;
}

const initialState: MaintenanceState = {
  requests: [],
  currentRequest: null,
  currentRequestId: null,
  maintenanceTypes: [],
  currentMaintenanceType: null,
  maintenanceAssignments: [],
  currentAssignment: null,
  availableAssignees: [],
  selectedCompanyId: null,
  filters: {
    page: 1,
    limit: 10,
  },
  pagination: {
    currentPage: 1,
    totalPages: 0,
    totalItems: 0,
    itemsPerPage: 10,
  },
  maintenanceStats: null,
  stats: null,
  isLoading: false,
  isLoadingTypes: false,
  isLoadingAssignments: false,
  isLoadingAssignees: false,
  isLoadingStats: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  error: null,
  success: null,
};

export const createMaintenanceRequest = createAsyncThunk(
  "maintenance/createRequest",
  async (requestData: any, { rejectWithValue }) => {
    try {
      const response = await maintenanceService.createMaintenanceRequest(
        requestData
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.error || "Failed to create maintenance request"
      );
    }
  }
);

export const fetchMaintenanceRequests = createAsyncThunk(
  "maintenance/fetchRequests",
  async (params: any = {}, { rejectWithValue }) => {
    try {
      const response = await maintenanceService.getMaintenanceRequests(params);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.error || "Failed to fetch maintenance requests"
      );
    }
  }
);

export const fetchMaintenanceRequestById = createAsyncThunk(
  "maintenance/fetchRequestById",
  async (requestId: string, { rejectWithValue }) => {
    try {
      const response = await maintenanceService.getMaintenanceRequestById(
        requestId
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.error || "Failed to fetch maintenance request"
      );
    }
  }
);

export const updateMaintenanceRequest = createAsyncThunk(
  "maintenance/updateRequest",
  async (
    {
      requestId,
      updates,
    }: {
      requestId: string;
      updates: Partial<maintenanceService.MaintenanceRequest>;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await maintenanceService.updateMaintenanceRequest(
        requestId,
        updates
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.error || "Failed to update maintenance request"
      );
    }
  }
);

export const assignMaintenanceRequest = createAsyncThunk(
  "maintenance/assignRequest",
  async (
    { requestId, userId }: { requestId: string; userId: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await maintenanceService.assignMaintenanceRequest(
        requestId,
        userId
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.error || "Failed to assign maintenance request"
      );
    }
  }
);

export const updateMaintenanceStatus = createAsyncThunk(
  "maintenance/updateStatus",
  async (
    {
      requestId,
      status,
      notes,
      completionCode,
    }: { requestId: string; status: string; notes?: string; completionCode?: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await maintenanceService.updateMaintenanceStatus(
        requestId,
        status,
        notes,
        completionCode
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.error || "Failed to update request status");
    }
  }
);

export const addPublicComment = createAsyncThunk(
  "maintenance/addPublicComment",
  async (
    { requestId, comment }: { requestId: string; comment: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await maintenanceService.addPublicComment(
        requestId,
        comment
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.error || "Failed to add comment");
    }
  }
);

export const addInternalNote = createAsyncThunk(
  "maintenance/addInternalNote",
  async (
    { requestId, note }: { requestId: string; note: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await maintenanceService.addInternalNote(
        requestId,
        note
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.error || "Failed to add internal note");
    }
  }
);

export const addMedia = createAsyncThunk(
  "maintenance/addMedia",
  async (
    { requestId, mediaData }: { requestId: string; mediaData: any },
    { rejectWithValue }
  ) => {
    try {
      const response = await maintenanceService.addMedia(requestId, mediaData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.error || "Failed to add media");
    }
  }
);

export const getMaintenanceStats = createAsyncThunk(
  "maintenance/getStats",
  async (params: any = {}, { rejectWithValue }) => {
    try {
      const response = await maintenanceService.getMaintenanceStats(params);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.error || "Failed to fetch maintenance statistics"
      );
    }
  }
);

// Maintenance Type Async Thunks
export const fetchMaintenanceTypes = createAsyncThunk(
  "maintenance/fetchTypes",
  async (
    { companyId, isActive }: { companyId: string; isActive?: boolean },
    { rejectWithValue }
  ) => {
    try {
      const response = await maintenanceService.getMaintenanceTypes(
        companyId,
        isActive
      );
      return { data: response.data, companyId };
    } catch (error: any) {
      return rejectWithValue(
        error.error || "Failed to fetch maintenance types"
      );
    }
  }
);

export const fetchMaintenanceType = createAsyncThunk(
  "maintenance/fetchType",
  async (typeId: string, { rejectWithValue }) => {
    try {
      const response = await maintenanceService.getMaintenanceType(typeId);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.error || "Failed to fetch maintenance type");
    }
  }
);

export const createMaintenanceType = createAsyncThunk(
  "maintenance/createType",
  async (data: CreateMaintenanceTypeData, { rejectWithValue }) => {
    try {
      const response = await maintenanceService.createMaintenanceType(data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.error || "Failed to create maintenance type"
      );
    }
  }
);

export const updateMaintenanceType = createAsyncThunk(
  "maintenance/updateType",
  async (
    { id, data }: { id: string; data: Partial<CreateMaintenanceTypeData> },
    { rejectWithValue }
  ) => {
    try {
      const response = await maintenanceService.updateMaintenanceType(id, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.error || "Failed to update maintenance type"
      );
    }
  }
);

export const deleteMaintenanceType = createAsyncThunk(
  "maintenance/deleteType",
  async (id: string, { rejectWithValue }) => {
    try {
      await maintenanceService.deleteMaintenanceType(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.error || "Failed to delete maintenance type"
      );
    }
  }
);

export const toggleMaintenanceType = createAsyncThunk(
  "maintenance/toggleType",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await maintenanceService.toggleMaintenanceType(id);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.error || "Failed to toggle maintenance type"
      );
    }
  }
);

export const createDefaultMaintenanceTypes = createAsyncThunk(
  "maintenance/createDefaultTypes",
  async (companyId: string, { rejectWithValue }) => {
    try {
      const response = await maintenanceService.createDefaultMaintenanceTypes(
        companyId
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.error || "Failed to create default maintenance types"
      );
    }
  }
);

// Inline assignee and watcher management thunks
export const addMaintenanceTypeAssignee = createAsyncThunk(
  "maintenance/addTypeAssignee",
  async (
    {
      maintenanceTypeId,
      userId,
    }: { maintenanceTypeId: string; userId: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await maintenanceService.addMaintenanceTypeAssignee(
        maintenanceTypeId,
        userId
      );
      return { maintenanceTypeId, data: response.data };
    } catch (error: any) {
      return rejectWithValue(error.error || "Failed to add assignee");
    }
  }
);

export const removeMaintenanceTypeAssignee = createAsyncThunk(
  "maintenance/removeTypeAssignee",
  async (maintenanceTypeId: string, { rejectWithValue }) => {
    try {
      await maintenanceService.removeMaintenanceTypeAssignee(maintenanceTypeId);
      return { maintenanceTypeId };
    } catch (error: any) {
      return rejectWithValue(error.error || "Failed to remove assignee");
    }
  }
);

export const addMaintenanceTypeWatcher = createAsyncThunk(
  "maintenance/addTypeWatcher",
  async (
    {
      maintenanceTypeId,
      userId,
    }: { maintenanceTypeId: string; userId: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await maintenanceService.addMaintenanceTypeWatcher(
        maintenanceTypeId,
        userId
      );
      return { maintenanceTypeId, data: response.data };
    } catch (error: any) {
      return rejectWithValue(error.error || "Failed to add watcher");
    }
  }
);

export const removeMaintenanceTypeWatcher = createAsyncThunk(
  "maintenance/removeTypeWatcher",
  async (
    {
      maintenanceTypeId,
      userId,
    }: { maintenanceTypeId: string; userId: string },
    { rejectWithValue }
  ) => {
    try {
      await maintenanceService.removeMaintenanceTypeWatcher(
        maintenanceTypeId,
        userId
      );
      return { maintenanceTypeId, userId };
    } catch (error: any) {
      return rejectWithValue(error.error || "Failed to remove watcher");
    }
  }
);

// Maintenance Assignment Thunks
export const fetchMaintenanceAssignments = createAsyncThunk(
  "maintenance/fetchAssignments",
  async (
    { companyId, isActive }: { companyId: string; isActive?: boolean },
    { rejectWithValue }
  ) => {
    try {
      const response = await maintenanceService.getMaintenanceAssignments(
        companyId,
        isActive
      );
      return { data: response.data, companyId };
    } catch (error: any) {
      return rejectWithValue(
        error.error || "Failed to fetch maintenance assignments"
      );
    }
  }
);

export const fetchAssignmentByType = createAsyncThunk(
  "maintenance/fetchAssignmentByType",
  async (
    {
      companyId,
      maintenanceType,
    }: { companyId: string; maintenanceType: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await maintenanceService.getAssignmentByType(
        companyId,
        maintenanceType
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.error || "Failed to fetch assignment by type"
      );
    }
  }
);

export const createOrUpdateAssignment = createAsyncThunk(
  "maintenance/createOrUpdateAssignment",
  async (assignmentData: any, { rejectWithValue }) => {
    try {
      const response = await maintenanceService.createOrUpdateAssignment(
        assignmentData
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.error || "Failed to create/update assignment"
      );
    }
  }
);

export const updateAssignmentSettings = createAsyncThunk(
  "maintenance/updateAssignmentSettings",
  async (
    { id, settings }: { id: string; settings: any },
    { rejectWithValue }
  ) => {
    try {
      const response = await maintenanceService.updateAssignmentSettings(
        id,
        settings
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.error || "Failed to update assignment settings"
      );
    }
  }
);

export const addAssignee = createAsyncThunk(
  "maintenance/addAssignee",
  async (
    { assignmentId, assigneeData }: { assignmentId: string; assigneeData: any },
    { rejectWithValue }
  ) => {
    try {
      const response = await maintenanceService.addAssignee(
        assignmentId,
        assigneeData
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.error || "Failed to add assignee");
    }
  }
);

export const removeAssignee = createAsyncThunk(
  "maintenance/removeAssignee",
  async (
    { assignmentId, userId }: { assignmentId: string; userId: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await maintenanceService.removeAssignee(
        assignmentId,
        userId
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.error || "Failed to remove assignee");
    }
  }
);

export const updateAssigneeStatus = createAsyncThunk(
  "maintenance/updateAssigneeStatus",
  async (
    {
      assignmentId,
      userId,
      isActive,
    }: { assignmentId: string; userId: string; isActive: boolean },
    { rejectWithValue }
  ) => {
    try {
      const response = await maintenanceService.updateAssigneeStatus(
        assignmentId,
        userId,
        isActive
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.error || "Failed to update assignee status");
    }
  }
);

export const addWatcher = createAsyncThunk(
  "maintenance/addWatcher",
  async (
    { assignmentId, watcherData }: { assignmentId: string; watcherData: any },
    { rejectWithValue }
  ) => {
    try {
      const response = await maintenanceService.addWatcher(
        assignmentId,
        watcherData
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.error || "Failed to add watcher");
    }
  }
);

export const removeWatcher = createAsyncThunk(
  "maintenance/removeWatcher",
  async (
    { assignmentId, userId }: { assignmentId: string; userId: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await maintenanceService.removeWatcher(
        assignmentId,
        userId
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.error || "Failed to remove watcher");
    }
  }
);

export const fetchAvailableAssignees = createAsyncThunk(
  "maintenance/fetchAvailableAssignees",
  async (companyId: string, { rejectWithValue }) => {
    try {
      const response = await maintenanceService.getAvailableAssignees(
        companyId
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.error || "Failed to fetch available assignees"
      );
    }
  }
);

const maintenanceSlice = createSlice({
  name: "maintenance",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = null;
    },
    setSelectedCompany: (state, action: PayloadAction<string>) => {
      state.selectedCompanyId = action.payload;
    },
    setFilters: (state, action: PayloadAction<any>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        page: 1,
        limit: 10,
      };
    },
    setCurrentRequest: (
      state,
      action: PayloadAction<MaintenanceRequest | null>
    ) => {
      state.currentRequest = action.payload;
      state.currentRequestId = action.payload?._id || null;
    },
    setCurrentMaintenanceType: (
      state,
      action: PayloadAction<MaintenanceType | null>
    ) => {
      state.currentMaintenanceType = action.payload;
    },
    clearRequests: (state) => {
      state.requests = [];
      state.currentRequest = null;
      state.currentRequestId = null;
      state.pagination = initialState.pagination;
    },
    clearMaintenanceTypes: (state) => {
      state.maintenanceTypes = [];
      state.currentMaintenanceType = null;
    },
    updateRequestInList: (state, action: PayloadAction<MaintenanceRequest>) => {
      const index = state.requests.findIndex(
        (request) => request._id === action.payload._id
      );
      if (index !== -1) {
        state.requests[index] = action.payload;
      }
    },
    updateMaintenanceTypeInList: (
      state,
      action: PayloadAction<MaintenanceType>
    ) => {
      const index = state.maintenanceTypes.findIndex(
        (type) => type._id === action.payload._id
      );
      if (index !== -1) {
        state.maintenanceTypes[index] = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Create Maintenance Request
      .addCase(createMaintenanceRequest.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createMaintenanceRequest.fulfilled, (state, action) => {
        state.isCreating = false;
        if (action.payload) {
          state.requests.unshift(action.payload);
          state.currentRequest = action.payload;
          state.error = null;
        }
      })
      .addCase(createMaintenanceRequest.rejected, (state, action) => {
        state.isCreating = false;
        state.error =
          (action.payload as string) || "Failed to create maintenance request";
      })
      // Fetch Maintenance Requests
      .addCase(fetchMaintenanceRequests.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMaintenanceRequests.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload && action.payload.data) {
          state.requests = action.payload.data;
          if (action.payload.pagination) {
            state.pagination = {
              ...action.payload.pagination,
              totalItems: action.payload.totalCount,
            };
          }
          state.error = null;
        }
      })
      .addCase(fetchMaintenanceRequests.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          (action.payload as string) || "Failed to fetch maintenance requests";
      })
      // Fetch Maintenance Request By ID
      .addCase(fetchMaintenanceRequestById.pending, (state, action) => {
        state.isLoading = true;
        state.error = null;
        state.currentRequestId = action.meta.arg;
      })
      .addCase(fetchMaintenanceRequestById.fulfilled, (state, action) => {
        if (state.currentRequestId === action.meta.arg) {
          state.isLoading = false;
          if (action.payload) {
            state.currentRequest = action.payload;
            state.error = null;
          }
        }
      })
      .addCase(fetchMaintenanceRequestById.rejected, (state, action) => {
        if (state.currentRequestId === action.meta.arg) {
          state.isLoading = false;
          state.error =
            (action.payload as string) || "Failed to fetch maintenance request";
        }
      })
      // Update Maintenance Request
      .addCase(updateMaintenanceRequest.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateMaintenanceRequest.fulfilled, (state, action) => {
        state.isUpdating = false;
        if (action.payload) {
          const request = action.payload;
          state.currentRequest = request;
          const index = state.requests.findIndex((r) => r._id === request._id);
          if (index !== -1) {
            state.requests[index] = request;
          }
          state.error = null;
        }
      })
      .addCase(updateMaintenanceRequest.rejected, (state, action) => {
        state.isUpdating = false;
        state.error =
          (action.payload as string) || "Failed to update maintenance request";
      })
      // Assign Maintenance Request
      .addCase(assignMaintenanceRequest.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(assignMaintenanceRequest.fulfilled, (state, action) => {
        state.isUpdating = false;
        if (action.payload) {
          const request = action.payload;
          state.currentRequest = request;
          const index = state.requests.findIndex((r) => r._id === request._id);
          if (index !== -1) {
            state.requests[index] = request;
          }
          state.error = null;
        }
      })
      .addCase(assignMaintenanceRequest.rejected, (state, action) => {
        state.isUpdating = false;
        state.error =
          (action.payload as string) || "Failed to assign maintenance request";
      })
      // Update Maintenance Status
      .addCase(updateMaintenanceStatus.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateMaintenanceStatus.fulfilled, (state, action) => {
        state.isUpdating = false;
        if (action.payload) {
          const request = action.payload;
          state.currentRequest = request;
          const index = state.requests.findIndex((r) => r._id === request._id);
          if (index !== -1) {
            state.requests[index] = request;
          }
          state.error = null;
        }
      })
      .addCase(updateMaintenanceStatus.rejected, (state, action) => {
        state.isUpdating = false;
        // state.error =
        //   (action.payload as string) || "Failed to update request status";
      })
      // Fetch Maintenance Types
      .addCase(fetchMaintenanceTypes.pending, (state) => {
        state.isLoadingTypes = true;
        state.error = null;
      })
      .addCase(fetchMaintenanceTypes.fulfilled, (state, action) => {
        state.isLoadingTypes = false;
        if (action.payload) {
          state.maintenanceTypes = action.payload.data || [];
          state.selectedCompanyId = action.payload.companyId;
          state.error = null;
        }
      })
      .addCase(fetchMaintenanceTypes.rejected, (state, action) => {
        state.isLoadingTypes = false;
        state.error =
          (action.payload as string) || "Failed to fetch maintenance types";
      })
      // Fetch Single Maintenance Type
      .addCase(fetchMaintenanceType.pending, (state) => {
        state.isLoadingTypes = true;
        state.error = null;
      })
      .addCase(fetchMaintenanceType.fulfilled, (state, action) => {
        state.isLoadingTypes = false;
        if (action.payload) {
          state.currentMaintenanceType = action.payload;
          state.error = null;
        }
      })
      .addCase(fetchMaintenanceType.rejected, (state, action) => {
        state.isLoadingTypes = false;
        state.error =
          (action.payload as string) || "Failed to fetch maintenance type";
      })
      // Create Maintenance Type
      .addCase(createMaintenanceType.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createMaintenanceType.fulfilled, (state, action) => {
        state.isCreating = false;
        if (action.payload) {
          state.maintenanceTypes.push(action.payload);
          state.success = "Maintenance type created successfully";
          state.error = null;
        }
      })
      .addCase(createMaintenanceType.rejected, (state, action) => {
        state.isCreating = false;
        state.error =
          (action.payload as string) || "Failed to create maintenance type";
      })
      // Update Maintenance Type
      .addCase(updateMaintenanceType.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateMaintenanceType.fulfilled, (state, action) => {
        state.isUpdating = false;
        if (action.payload) {
          const updatedType = action.payload;
          const index = state.maintenanceTypes.findIndex(
            (type) => type._id === updatedType._id
          );
          if (index !== -1) {
            state.maintenanceTypes[index] = updatedType;
          }
          state.currentMaintenanceType = updatedType;
          state.success = "Maintenance type updated successfully";
          state.error = null;
        }
      })
      .addCase(updateMaintenanceType.rejected, (state, action) => {
        state.isUpdating = false;
        state.error =
          (action.payload as string) || "Failed to update maintenance type";
      })
      // Delete Maintenance Type
      .addCase(deleteMaintenanceType.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteMaintenanceType.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.maintenanceTypes = state.maintenanceTypes.filter(
          (type) => type._id !== action.payload
        );
        state.success = "Maintenance type deleted successfully";
        state.error = null;
      })
      .addCase(deleteMaintenanceType.rejected, (state, action) => {
        state.isDeleting = false;
        state.error =
          (action.payload as string) || "Failed to delete maintenance type";
      })
      // Toggle Maintenance Type
      .addCase(toggleMaintenanceType.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(toggleMaintenanceType.fulfilled, (state, action) => {
        state.isUpdating = false;
        if (action.payload) {
          const toggledType = action.payload;
          const index = state.maintenanceTypes.findIndex(
            (type) => type._id === toggledType._id
          );
          if (index !== -1) {
            state.maintenanceTypes[index] = toggledType;
          }
          state.success = `Maintenance type ${
            toggledType.isActive ? "activated" : "deactivated"
          } successfully`;
          state.error = null;
        }
      })
      .addCase(toggleMaintenanceType.rejected, (state, action) => {
        state.isUpdating = false;
        state.error =
          (action.payload as string) || "Failed to toggle maintenance type";
      })
      // Create Default Types
      .addCase(createDefaultMaintenanceTypes.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createDefaultMaintenanceTypes.fulfilled, (state, action) => {
        state.isCreating = false;
        if (action.payload) {
          state.maintenanceTypes = [
            ...state.maintenanceTypes,
            ...action.payload,
          ];
          state.success = "Default maintenance types created successfully";
          state.error = null;
        }
      })
      .addCase(createDefaultMaintenanceTypes.rejected, (state, action) => {
        state.isCreating = false;
        state.error =
          (action.payload as string) || "Failed to create default types";
      })
      // Add Assignee to Type
      .addCase(addMaintenanceTypeAssignee.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(addMaintenanceTypeAssignee.fulfilled, (state, action) => {
        state.isUpdating = false;
        state.success = "Assignee added successfully";
        state.error = null;
      })
      .addCase(addMaintenanceTypeAssignee.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = (action.payload as string) || "Failed to add assignee";
      })
      // Remove Assignee from Type
      .addCase(removeMaintenanceTypeAssignee.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(removeMaintenanceTypeAssignee.fulfilled, (state, action) => {
        state.isUpdating = false;
        state.success = "Assignee removed successfully";
        state.error = null;
      })
      .addCase(removeMaintenanceTypeAssignee.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = (action.payload as string) || "Failed to remove assignee";
      })
      // Add Watcher to Type
      .addCase(addMaintenanceTypeWatcher.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(addMaintenanceTypeWatcher.fulfilled, (state, action) => {
        state.isUpdating = false;
        state.success = "Watcher added successfully";
        state.error = null;
      })
      .addCase(addMaintenanceTypeWatcher.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = (action.payload as string) || "Failed to add watcher";
      })
      // Remove Watcher from Type
      .addCase(removeMaintenanceTypeWatcher.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(removeMaintenanceTypeWatcher.fulfilled, (state, action) => {
        state.isUpdating = false;
        state.success = "Watcher removed successfully";
        state.error = null;
      })
      .addCase(removeMaintenanceTypeWatcher.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = (action.payload as string) || "Failed to remove watcher";
      })
      // Fetch Maintenance Assignments
      .addCase(fetchMaintenanceAssignments.pending, (state) => {
        state.isLoadingAssignments = true;
        state.error = null;
      })
      .addCase(fetchMaintenanceAssignments.fulfilled, (state, action) => {
        state.isLoadingAssignments = false;
        if (action.payload && action.payload.data) {
          state.maintenanceAssignments = action.payload.data;
        }
        state.error = null;
      })
      .addCase(fetchMaintenanceAssignments.rejected, (state, action) => {
        state.isLoadingAssignments = false;
        state.error =
          (action.payload as string) || "Failed to fetch assignments";
      })
      // Fetch Assignment by Type
      .addCase(fetchAssignmentByType.pending, (state) => {
        state.isLoadingAssignments = true;
        state.error = null;
      })
      .addCase(fetchAssignmentByType.fulfilled, (state, action) => {
        state.isLoadingAssignments = false;
        if (action.payload) {
          state.currentAssignment = action.payload;
        } else {
          state.currentAssignment = null;
        }
        state.error = null;
      })
      .addCase(fetchAssignmentByType.rejected, (state, action) => {
        state.isLoadingAssignments = false;
        state.currentAssignment = null;
        state.error =
          (action.payload as string) || "Failed to fetch assignment by type";
      })
      // Create or Update Assignment
      .addCase(createOrUpdateAssignment.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createOrUpdateAssignment.fulfilled, (state, action) => {
        state.isCreating = false;
        if (action.payload) {
          const assignment = action.payload;
          const index = state.maintenanceAssignments.findIndex(
            (a) => a._id === assignment._id
          );
          if (index !== -1) {
            state.maintenanceAssignments[index] = assignment;
          } else {
            state.maintenanceAssignments.push(assignment);
          }
          state.currentAssignment = assignment;
        }
        state.success = "Assignment saved successfully";
        state.error = null;
      })
      .addCase(createOrUpdateAssignment.rejected, (state, action) => {
        state.isCreating = false;
        state.error = (action.payload as string) || "Failed to save assignment";
      })
      // Update Assignment Settings
      .addCase(updateAssignmentSettings.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateAssignmentSettings.fulfilled, (state, action) => {
        state.isUpdating = false;
        if (action.payload) {
          const assignment = action.payload;
          const index = state.maintenanceAssignments.findIndex(
            (a) => a._id === assignment._id
          );
          if (index !== -1) {
            state.maintenanceAssignments[index] = assignment;
          }
          if (state.currentAssignment?._id === assignment._id) {
            state.currentAssignment = assignment;
          }
        }
        state.success = "Settings updated successfully";
        state.error = null;
      })
      .addCase(updateAssignmentSettings.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = (action.payload as string) || "Failed to update settings";
      })
      // Add Assignee
      .addCase(addAssignee.fulfilled, (state, action) => {
        if (action.payload) {
          const assignment = action.payload;
          const index = state.maintenanceAssignments.findIndex(
            (a) => a._id === assignment._id
          );
          if (index !== -1) {
            state.maintenanceAssignments[index] = assignment;
          }
          if (state.currentAssignment?._id === assignment._id) {
            state.currentAssignment = assignment;
          }
        }
        state.success = "Assignee added successfully";
      })
      // Remove Assignee
      .addCase(removeAssignee.fulfilled, (state, action) => {
        if (action.payload) {
          const assignment = action.payload;
          const index = state.maintenanceAssignments.findIndex(
            (a) => a._id === assignment._id
          );
          if (index !== -1) {
            state.maintenanceAssignments[index] = assignment;
          }
          if (state.currentAssignment?._id === assignment._id) {
            state.currentAssignment = assignment;
          }
        }
        state.success = "Assignee removed successfully";
      })
      // Update Assignee Status
      .addCase(updateAssigneeStatus.fulfilled, (state, action) => {
        if (action.payload) {
          const assignment = action.payload;
          const index = state.maintenanceAssignments.findIndex(
            (a) => a._id === assignment._id
          );
          if (index !== -1) {
            state.maintenanceAssignments[index] = assignment;
          }
          if (state.currentAssignment?._id === assignment._id) {
            state.currentAssignment = assignment;
          }
        }
        state.success = "Assignee status updated successfully";
      })
      // Add Watcher
      .addCase(addWatcher.fulfilled, (state, action) => {
        if (action.payload) {
          const assignment = action.payload;
          const index = state.maintenanceAssignments.findIndex(
            (a) => a._id === assignment._id
          );
          if (index !== -1) {
            state.maintenanceAssignments[index] = assignment;
          }
          if (state.currentAssignment?._id === assignment._id) {
            state.currentAssignment = assignment;
          }
        }
        state.success = "Watcher added successfully";
      })
      // Remove Watcher
      .addCase(removeWatcher.fulfilled, (state, action) => {
        if (action.payload) {
          const assignment = action.payload;
          const index = state.maintenanceAssignments.findIndex(
            (a) => a._id === assignment._id
          );
          if (index !== -1) {
            state.maintenanceAssignments[index] = assignment;
          }
          if (state.currentAssignment?._id === assignment._id) {
            state.currentAssignment = assignment;
          }
        }
        state.success = "Watcher removed successfully";
      })
      // Fetch Available Assignees
      .addCase(fetchAvailableAssignees.pending, (state) => {
        state.isLoadingAssignees = true;
        state.error = null;
      })
      .addCase(fetchAvailableAssignees.fulfilled, (state, action) => {
        state.isLoadingAssignees = false;
        state.availableAssignees = action.payload || [];
        state.error = null;
      })
      .addCase(fetchAvailableAssignees.rejected, (state, action) => {
        state.isLoadingAssignees = false;
        state.error =
          (action.payload as string) || "Failed to fetch available assignees";
      })

      // Get Maintenance Stats
      .addCase(getMaintenanceStats.pending, (state) => {
        state.isLoadingStats = true;
        state.error = null;
      })
      .addCase(getMaintenanceStats.fulfilled, (state, action) => {
        state.isLoadingStats = false;
        state.maintenanceStats = action.payload;
        state.error = null;
      })
      .addCase(getMaintenanceStats.rejected, (state, action) => {
        state.isLoadingStats = false;
        state.error =
          (action.payload as string) ||
          "Failed to fetch maintenance statistics";
      });
  },
});

export const {
  clearError,
  clearSuccess,
  setSelectedCompany,
  setFilters,
  clearFilters,
  setCurrentRequest,
  setCurrentMaintenanceType,
  clearRequests,
  clearMaintenanceTypes,
  updateRequestInList,
  updateMaintenanceTypeInList,
} = maintenanceSlice.actions;

export default maintenanceSlice.reducer;
